import { BlocGraphique, BlocSuggestions, BlocTableau, PisteDeSuite } from '../../types/chat';
import { DataItem } from './DataGrid';

// Découpage d'une réponse de Nia : texte markdown et blocs `%%DATA%%` construits par le
// serveur. Gardes de type : le JSON vient du serveur, mais on ne l'affiche que s'il a
// bien la forme attendue (jamais de JSON brut à l'écran).

const MOTIF_BLOC = /(%%DATA%%[\s\S]*?%%DATA%%)/g;
const BALISE = /%%DATA%%/g;

const estObjet = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const estGraphique = (v: unknown): v is BlocGraphique =>
  estObjet(v) && v.type === 'chart' && Array.isArray(v.series) && (v.kind === 'line' || v.kind === 'bar');

const estTableau = (v: unknown): v is BlocTableau =>
  estObjet(v) && v.type === 'table' && Array.isArray(v.columns) && Array.isArray(v.rows);

const estPiste = (v: unknown): v is PisteDeSuite =>
  estObjet(v) && typeof v.label === 'string' && v.label.trim() !== '' && (v.kind === 'chart' || v.kind === 'question');

const estSuggestions = (v: unknown): v is BlocSuggestions =>
  estObjet(v) && v.type === 'suggestions' && Array.isArray(v.pistes);

export type Segment =
  | { type: 'texte'; texte: string }
  | { type: 'graphique'; bloc: BlocGraphique }
  | { type: 'tableau'; bloc: BlocTableau }
  | { type: 'grille'; items: DataItem[] };

export interface ReponseDecoupee {
  segments: Segment[];
  pistes: PisteDeSuite[];
}

export const decoupeReponse = (contenu: string): ReponseDecoupee => {
  const segments: Segment[] = [];
  let pistes: PisteDeSuite[] = [];
  if (!contenu) return { segments, pistes };

  contenu.split(MOTIF_BLOC).forEach((partie) => {
    if (!(partie.startsWith('%%DATA%%') && partie.endsWith('%%DATA%%') && partie.length > 16)) {
      if (partie.trim()) segments.push({ type: 'texte', texte: partie });
      return;
    }
    try {
      const data: unknown = JSON.parse(partie.replace(BALISE, '').trim());
      if (estSuggestions(data)) pistes = data.pistes.filter(estPiste);
      else if (estGraphique(data)) segments.push({ type: 'graphique', bloc: data });
      else if (estTableau(data)) segments.push({ type: 'tableau', bloc: data });
      else if (estObjet(data) && Array.isArray(data.items)) segments.push({ type: 'grille', items: data.items as DataItem[] });
      // Bloc de forme inconnue : ignoré.
    } catch (e) {
      console.error('Bloc de données illisible dans la réponse de Nia', e);
    }
  });
  return { segments, pistes };
};

// Texte seul d'une réponse (copie dans le presse-papiers) : sans aucun bloc.
export const texteSeul = (contenu: string): string =>
  (contenu || '').replace(MOTIF_BLOC, '').replace(BALISE, '').replace(/\n{3,}/g, '\n\n').trim();
